import { AppState } from "../state/app.state";
import { Owner, OwnerSearch } from "../models/owner.model";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { map, tap } from "rxjs/operators";
import { Observable, Subscription } from "rxjs";
import { Response } from "../models/response.model";
import { DossierState } from "src/app/dossiers/dossier.state";
import { MessageService } from "./message.service";
import { VlokExemption, VlokResponse } from "../models/vlok.model";

const httpOptions = {
  headers: new HttpHeaders({ "Content-Type": "application/json" }),
};

@Injectable({
  providedIn: "root",
})
export class MagdaService implements OnDestroy {
  private subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private appState: AppState,
    private dossierState: DossierState,
    private messageService: MessageService
  ) {}

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  addOwnersFromMagda(dossierCode: string, loopThroughAllPartitions: boolean): Observable<Response> {
    let queryParams = new HttpParams().set('loopThroughAllPartitions', loopThroughAllPartitions);
    return this.http.get<Response>(`${this.appState.baseApiUrl}/magda/building-owners/add/${dossierCode}`, {
      ...httpOptions,
      params: queryParams,
    }).pipe(
      map((response: Response) => {
        return response;
      })
    );
  }

  addOwnersFromMagdaWithPartitionsLoop(dossierCode: string): Observable<Response> {
    return this.http.get<Response>(`${this.appState.baseApiUrl}/magda/building-owners/add/${dossierCode}/loop`, httpOptions).pipe(
      map((response: Response) => {
        return response;
      })
    );
  }

  syncVlok(dossierCode: string, vlokPandCode: string): Observable<Response> {
    const queryParams: any = {};
    if (vlokPandCode !== null && vlokPandCode !== undefined && vlokPandCode !== '') {
      queryParams.vlokPandCode = vlokPandCode;
    }

    return this.http.get<Response>(`${this.appState.baseApiUrl}/magda/vlok/sync/${dossierCode}`, {
      ...httpOptions,
      params: queryParams
      }).pipe(
      map((response: Response) => {
        if (!response.Success) {
          this.messageService.showSnackBarError(`Het synchroniseren met VLOK is mislukt: ${response.Message}`);
        }
        
        return response;
      })
    );
  }

  updateOwnersFromMagda(): Observable<any> {
    return this.http.post<any>(`${this.appState.baseApiUrl}/background/update-magda-owners`, httpOptions);
  }

  getAllVlok(dossierCode: string): Observable<VlokResponse> {
    return this.http.get<Response>(`${this.appState.baseApiUrl}/magda/vlok/${dossierCode}`, httpOptions).pipe(
      map((response: Response) => {
        if (!response.Success) {
          return;
        }

        this.dossierState.vlokInformation$.next(response.Data);
        
        return response.Data;
      })
    );
  }

  getVlokBasic(dossierCode: string): Observable<VlokResponse> {
    return this.http.get<Response>(`${this.appState.baseApiUrl}/magda/vlok-basic/${dossierCode}`, httpOptions).pipe(
      map((response: Response) => {
        if (!response.Success) {
          this.messageService.showSnackBarError(`Ophalen van vlok-basisinformatie is mislukt: ${response.Message}`);
          return null;
        }

        return response.Data;
      })
    ); 
  }

  getVlokExemptionList(): Observable<VlokExemption[]> {
    return this.http.get<VlokExemption[]>(`${this.appState.baseApiUrl}/magda/vlok/exemptions`, httpOptions);
  }

  deleteVlokExemption(exemptionId: number): Observable<any> {
    return this.http.delete(`${this.appState.baseApiUrl}/magda/vlok/exemptions/${exemptionId}`, httpOptions);
  }

  resyncVlokExemption(id: number): Observable<Response> {
    return this.http.post<Response>(`${this.appState.baseApiUrl}/magda/vlok/exemptions/resync/${id}`, httpOptions);
  }

  resyncVlokExemptions(): Observable<any> {
    return this.http.post(`${this.appState.baseApiUrl}/magda/vlok/exemptions/resync`, httpOptions);
  }
}
